import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { EMAILJS } from '../emailjsConfig';
import './ContactPage.css';

const INITIAL = { name: '', company: '', email: '', phone: '', needs: '', budget: '', message: '' };

const ContactPage = () => {
    const [form, setForm] = useState(INITIAL);
    const [status, setStatus] = useState('idle'); // idle | sending | success | error

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');

        try {
            await emailjs.send(
                EMAILJS.serviceId,
                EMAILJS.templateId,
                {
                    from_name:    form.name,
                    from_email:   form.email,
                    company:      form.company,
                    phone:        form.phone,
                    needs:        form.needs,
                    budget:       form.budget,
                    message:      form.message,
                    reply_to:     form.email,
                },
                EMAILJS.publicKey
            );
            setStatus('success');
            setForm(INITIAL);
        } catch {
            setStatus('error');
        }
    };

    const isSending = status === 'sending';

    return (
        <motion.div
            className="contact-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>Contact Us | Kodebuild — Get a Free Consultation</title>
                <meta name="description" content="Ready to start your next project? Contact Kodebuild for a consultation on web development, mobile apps, or digital strategy. We're here to help." />
                <link rel="canonical" href="https://kodebuildtech.com/contact" />
                {/* Open Graph */}
                <meta property="og:title" content="Contact Kodebuild — Get a Free Consultation" />
                <meta property="og:description" content="Ready to start your next project? Contact Kodebuild for a consultation on web development, mobile apps, or digital strategy. We respond within 24 hours." />
                <meta property="og:url" content="https://kodebuildtech.com/contact" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Contact Kodebuild — Get a Free Consultation" />
                <meta name="twitter:description" content="Ready to build something great? Get in touch with the Kodebuild team today." />
                <meta name="twitter:image" content="https://kodebuildtech.com/Logo/kodebuild_png.png" />
            </Helmet>

            <div className="container">
                <header className="contact-header">
                    <span className="section-eyebrow">Get in Touch</span>
                    <h1>Let&rsquo;s Build <span>Something Great</span></h1>
                    <p>Have a project in mind? We&rsquo;d love to hear from you. We typically respond within 24 hours.</p>
                </header>

                <div className="contact-grid">
                    {/* ── Info panel ── */}
                    <div className="contact-info-panel">
                        <div className="info-item">
                            <div className="info-icon"><Mail /></div>
                            <div className="info-text">
                                <h4>Email Us</h4>
                                <p><a href="mailto:info@kodebuildtech.com">info@kodebuildtech.com</a></p>
                                <p className="email-alt"><a href="mailto:kodebuildtech@gmail.com">kodebuildtech@gmail.com</a></p>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon"><Phone /></div>
                            <div className="info-text">
                                <h4>Call Us</h4>
                                <p>+263 77 730 5046</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <div className="info-icon"><MapPin /></div>
                            <div className="info-text">
                                <h4>Location</h4>
                                <p>Harare, Zimbabwe &bull; Global Services</p>
                            </div>
                        </div>

                        <div className="response-note">
                            <span className="response-dot" />
                            We usually respond within <strong>24 hours</strong> on business days.
                        </div>
                    </div>

                    {/* ── Form panel ── */}
                    <AnimatePresence mode="wait">
                        {status === 'success' ? (
                            <motion.div
                                key="success"
                                className="form-feedback form-feedback--success"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <CheckCircle size={48} strokeWidth={1.5} />
                                <h3>Message Sent!</h3>
                                <p>Thanks for reaching out, <strong>{form.name || 'there'}</strong>. We&rsquo;ve received your message and will get back to you shortly.</p>
                                <button
                                    className="btn-primary"
                                    onClick={() => setStatus('idle')}
                                >
                                    Send Another Message
                                </button>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                className="contact-form"
                                onSubmit={handleSubmit}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="name">Full Name <span className="required">*</span></label>
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            placeholder="John Doe"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                            disabled={isSending}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="company">Company Name</label>
                                        <input
                                            id="company"
                                            name="company"
                                            type="text"
                                            placeholder="Your Company"
                                            value={form.company}
                                            onChange={handleChange}
                                            disabled={isSending}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="email">Email Address <span className="required">*</span></label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={form.email}
                                            onChange={handleChange}
                                            required
                                            disabled={isSending}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone">Phone Number</label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            type="tel"
                                            placeholder="+263 77 000 0000"
                                            value={form.phone}
                                            onChange={handleChange}
                                            disabled={isSending}
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="needs">What do you need help with?</label>
                                        <select
                                            id="needs"
                                            name="needs"
                                            value={form.needs}
                                            onChange={handleChange}
                                            disabled={isSending}
                                        >
                                            <option value="">Select a service...</option>
                                            <option value="Web Development">Web Development</option>
                                            <option value="Mobile Application">Mobile Application</option>
                                            <option value="Custom Software">Custom Software</option>
                                            <option value="Cybersecurity">Cybersecurity</option>
                                            <option value="Cloud/DevOps">Cloud / DevOps</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="budget">Estimated Budget</label>
                                        <select
                                            id="budget"
                                            name="budget"
                                            value={form.budget}
                                            onChange={handleChange}
                                            disabled={isSending}
                                        >
                                            <option value="">Select a range...</option>
                                            <option value="< $1,000">Less than $1,000</option>
                                            <option value="$1,000 - $5,000">$1,000 - $5,000</option>
                                            <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                                            <option value="$10,000+">$10,000+</option>
                                            <option value="Not Sure">Not sure yet</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">Message <span className="required">*</span></label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        placeholder="Tell us about your project, goals, and timeline..."
                                        rows="6"
                                        value={form.message}
                                        onChange={handleChange}
                                        required
                                        disabled={isSending}
                                    />
                                </div>

                                {status === 'error' && (
                                    <div className="form-error-banner">
                                        <AlertCircle size={16} />
                                        Something went wrong. Please try again or email us directly at{' '}
                                        <a href="mailto:info@kodebuildtech.com">info@kodebuildtech.com</a>.
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn-primary glow-hover submit-btn"
                                    disabled={isSending}
                                >
                                    {isSending ? (
                                        <>
                                            <Loader size={18} className="spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send Message <Send size={18} />
                                        </>
                                    )}
                                </button>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
};

export default ContactPage;
